<template>
  <div class="tw-flex tw-flex-row">
    <div
      v-for="(option, index) in props.options"
      :key="index"
      class="tw-flex tw-items-center"
      :data-test-id="props.dataTestId"
    >
      <input
        :id="`${customId}-${option.value}`"
        type="radio"
        :name="`${customId}-name`"
        :class="inputClasses"
        :checked="option.value === selected"
        :disabled="props.disabled"
        @change="emitRadio(option.value)"
      />
      <label
        :for="`${customId}-${option.value}`"
        :data-test-id="`${props.dataTestId}-${option.label}`"
        :class="[
          labelClasses,
          {
            'tw-text-sm': props.size === 'small',
            'tw-rounded-l tw-border-2': index === 0 && props.button,
            'tw-rounded-r tw-border-b-2 tw-border-r-2 tw-border-t-2':
              index === options.length - 1 && props.button,
            'tw-border-b-2 tw-border-r-2 tw-border-t-2':
              index !== options.length - 1 && index !== 0 && props.button,
          },
        ]"
        >{{ option.label }}</label
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue";

import type { FormRadioGroup } from "./types";

export interface FormRadioGroupProps {
  dataTestId?: string;
  id?: string;
  options: FormRadioGroup[];
  selectedValue?: string | boolean | number;
  button?: boolean;
  disabled?: boolean;
  size?: "small" | "medium";
}

const props = withDefaults(defineProps<FormRadioGroupProps>(), {
  button: false,
  dataTestId: "",
  disabled: false,
  size: "medium",
  id: "",
  selectedValue: undefined,
});

const emit = defineEmits<{
  (event: "update:selected", value: string | boolean | number): void;
}>();

const inputVariant = {
  radio:
    "tw-bg-white tw-border-dorian-gray-500 tw-border-dorian-gray-70 tw-my-2 tw-ml-4 tw-mr-2 tw-h-5 tw-w-5 tw-cursor-pointer tw-border-2 tw-border-solid tw-text-secondary-500 checked:tw-border-secondary-500 focus:tw-ring-secondary-500 focus:tw-ring-offset-white",
  button: "tw-sr-only tw-invisible tw-peer",
};

const labelVariant = {
  radio: "tw-text-base tw-cursor-pointer tw-m-0",
  button:
    "tw-m-0 tw-text-secondary-500 tw-font-bold peer-checked:tw-bg-secondary-500 tw-border-secondary-500 peer-checked:tw-text-white tw-flex tw-px-3 tw-py-2 tw-cursor-pointer hover:tw-bg-secondary-200 hover:tw-text-secondary-600",
};

const selected = ref(props.selectedValue);
const customId = ref(
  props.id ? props.id : (Math.random() + 1).toString(36).substring(2),
);

const inputClasses = computed(() => {
  return props.button ? [inputVariant.button] : [inputVariant.radio];
});

const labelClasses = computed(() => {
  return props.button ? [labelVariant.button] : [labelVariant.radio];
});

watch(
  () => props.selectedValue,
  () => {
    selected.value = props.selectedValue;
  },
);

const emitRadio = (newValue: string | boolean) => {
  emit("update:selected", newValue);
};
</script>
